// Main App shell: routing, auth gate, theme + accent, sidebar, topbar.

import { useState, useEffect, useCallback, Fragment, type ReactNode } from 'react';
import { I } from './components/icons';
import { ToastProvider, Logo, Avatar, IconBtn, Empty } from './components/ui';
import {
  useTweaks, TweaksPanel, TweakSection, TweakRadio,
} from './components/TweaksPanel';
import { onVisible } from './hooks/onVisible';
import { GlobalTooltip } from './components/Tooltip';
import { LoginScreen, Dashboard, NodesScreen, AuditLog, EventsScreen, TrashScreen, Settings } from './screens/AuthDash';
import { ClientsList, ClientDetail, AddClientWizard } from './screens/Clients';
import { BypassRulesScreen } from './screens/BypassRules';
import { RegistriesScreen } from './screens/Registries';
import { StorageServersScreen } from './screens/StorageServers';
import { S3AccountsScreen } from './screens/S3Accounts';
import { AccessTokensScreen } from './screens/AccessTokens';
import { GlobalAppsScreen, GlobalAppDetail } from './screens/GlobalApps';
import { ServicesLookupScreen } from './screens/ServicesLookup';
import { loadState, saveState } from './data/mock';
import { fetchTenants, fetchSettings, fetchMe, fetchVersion, logout as apiLogout, ApiError, UI_COMMIT } from './api/client';
import { useUrlRoute } from './hooks/useUrlRoute';
import { OperatorModeProvider, useOperatorMode } from './hooks/useOperatorMode';
import type { AppState, Route, AuthUser } from './types';

const TWEAK_DEFAULTS = {
  theme: 'dark' as 'dark' | 'light',
  accent: 'indigo' as 'indigo' | 'emerald' | 'amber',
};

// Truncate a git SHA to a short 7-char form; passes through "dev"/"?".
const short = (sha: string) => (/^[0-9a-f]{7,}$/i.test(sha) ? sha.slice(0, 7) : sha);

// Small muted chip showing which commit the UI and API were built from, so an
// operator can confirm a deploy is live and FE/BE are in sync. UI commit is
// baked in at build (falls back to "dev"); API commit is fetched once and
// degrades to "?" if the backend is unreachable.
function VersionChip() {
  const [api, setApi] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchVersion().then((v) => {
      if (!alive) return;
      if (v?.commit) setApi(v.commit);
      else setFailed(true);
    });
    return () => { alive = false; };
  }, []);
  const apiLabel = api ? short(api) : failed ? '?' : '…';
  return (
    <span className="mono muted"
          style={{ fontSize: 11, whiteSpace: 'nowrap' }}
          title={`UI build ${UI_COMMIT} · API build ${api ?? (failed ? 'unavailable' : 'loading')}`}>
      UI {short(UI_COMMIT)} · API {apiLabel}
    </span>
  );
}

// SidebarNav renders the nav groups. It's a context consumer (rendered inside
// OperatorModeProvider) so it can hide operator-only entries — the Access
// tokens and MinIO servers tabs — when operator mode is locked (client view).
function SidebarNav({ navGroups, routePage, onNav }: {
  navGroups: Array<{ section: string; items: Array<{ id: string; icon: string; label: string; badge?: number }> }>;
  routePage: string;
  onNav: (r: Route) => void;
}) {
  const { unlocked } = useOperatorMode();
  // MinIO servers belongs here for the same reason as access tokens: it holds
  // the root credentials of whole storage servers, which read, delete and
  // re-key every tenant's buckets — not something a client view should offer.
  const OPERATOR_ONLY = new Set(['access-tokens', 'minio-servers']);
  return (
    <>
      {navGroups.map((g) => {
        const items = g.items.filter((n) => unlocked || !OPERATOR_ONLY.has(n.id));
        if (items.length === 0) return null;
        return (
          <div key={g.section}>
            <div className="sidebar-section">{g.section}</div>
            {items.map((n) => {
              const Comp = I[n.icon];
              return (
                <button key={n.id}
                        className={'nav-item' + ((routePage === n.id || (n.id === 'clients' && routePage === 'client-detail') || (n.id === 'global-apps' && routePage === 'global-app-detail')) ? ' active' : '')}
                        onClick={() => onNav({ page: n.id } as Route)}>
                  <Comp size={14}/>
                  <span>{n.label}</span>
                  {n.badge != null && <span className="nav-badge">{n.badge}</span>}
                </button>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

// OperatorOnly gates a screen behind operator mode — a consumer, so it reacts to
// toggling. Used so hitting an operator-only route directly (URL, or while it's
// open when operator mode is locked) doesn't expose the screen.
function OperatorOnly({ children }: { children: ReactNode }) {
  const { unlocked } = useOperatorMode();
  if (!unlocked) {
    return <Empty title="Operator tools only"
                  message="This section is hidden in client view. Unlock operator mode to manage it."/>;
  }
  return <>{children}</>;
}

export default function App() {
  // Initial state comes from localStorage (or built-in mock on first load).
  // Once /api/v1/tenants resolves, the real backend data overwrites
  // state.clients. Globals / configs / audit stay on mock data until their
  // endpoints land. Keeping the pre-fetch fallback avoids the "blank screen
  // during the few ms before the fetch resolves" race condition.
  const [state, setState] = useState<AppState>(() => loadState());
  const [route, setRoute] = useUrlRoute();
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const [addOpen, setAddOpen] = useState(false);
  const [tenantsError, setTenantsError] = useState<string | null>(null);
  // False until the first /tenants fetch resolves. Used to show a loading
  // state instead of an empty client list on a fresh load (empty localStorage).
  const [tenantsLoaded, setTenantsLoaded] = useState(false);

  useEffect(() => { saveState(state); }, [state]);

  useEffect(() => {
    document.documentElement.dataset.theme = t.theme;
    document.documentElement.dataset.accent = t.accent;
  }, [t.theme, t.accent]);

  // On first mount, ask the backend whether a session cookie is already
  // valid (browser kept it across reloads). If yes, sign the user in
  // automatically; if not, just leave them on the login screen.
  const [authChecked, setAuthChecked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => { if (!cancelled) setState((s) => ({ ...s, auth: { email: me.email, name: me.displayName || me.email } })); })
      .catch(() => { /* no valid session — fall through to login */ })
      .finally(() => { if (!cancelled) setAuthChecked(true); });
    return () => { cancelled = true; };
    // run once at mount; deliberately empty deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any 401 from an authenticated /api/v1 fetch means the session died on
  // the server side — sign the user out client-side so they go back to login.
  const onApiError = useCallback((err: unknown) => {
    if (err instanceof ApiError && err.status === 401) {
      setState((s) => ({ ...s, auth: null }));
    }
  }, []);

  // Keep the tenant list fresh on tenants-related routes: fetch on sign-in /
  // navigation, then poll every 15s. The poll is what surfaces backend
  // status changes the user didn't trigger — e.g. the status reconciler
  // flipping a tenant provisioning → ready once its pods go Healthy (~20s
  // server-side). Without it the badge would stay "provisioning" until a
  // manual refresh.
  const onTenantsRoute = route.page === 'clients' || route.page === 'client-detail' || route.page === 'dashboard';
  useEffect(() => {
    if (!state.auth) return;
    let cancelled = false;
    const load = () => {
      fetchTenants()
        .then((clients) => { if (!cancelled) { setState((s) => ({ ...s, clients })); setTenantsError(null); } })
        .catch((err: unknown) => {
          if (cancelled) return;
          onApiError(err);
          const msg = err instanceof ApiError ? `${err.status || 'network'}: ${err.message}` : String(err);
          console.error('failed to load tenants:', err);
          setTenantsError(msg);
        })
        .finally(() => { if (!cancelled) setTenantsLoaded(true); });
    };
    // Always load once when authed so state.clients is available to every screen
    // that reads it — e.g. the Bypass-rules tenant dropdown — even on a direct
    // deep-link / refresh onto a non-tenants route. Only POLL on tenants routes,
    // where the user watches status flip (reconciler provisioning→ready); polling
    // elsewhere would just be needless background fetches.
    load();
    const id = onTenantsRoute ? setInterval(load, 12000) : undefined;
    const off = onVisible(load);
    return () => { cancelled = true; if (id !== undefined) clearInterval(id); off(); };
  }, [state.auth, onTenantsRoute, onApiError]);

  // Load settings once after sign-in. They're cluster-wide config and
  // change rarely, so no point refetching on every navigation.
  useEffect(() => {
    if (!state.auth) return;
    let cancelled = false;
    fetchSettings()
      .then((settings) => { if (!cancelled) setState((s) => ({ ...s, settings: { ...s.settings, ...settings } })); })
      .catch((err) => { console.error('failed to load settings:', err); });
    return () => { cancelled = true; };
  }, [state.auth]);

  const nav = useCallback((r: Route & { tab?: string; service?: string }) => setRoute(r), []);

  // Public client portal — rendered before the admin auth gate so a client can
  // look up their services with only their BasicUser credentials, no operator
  // session required.
  if (route.page === 'services-lookup') {
    return (
      <ToastProvider>
        <ServicesLookupScreen/>
        <AppTweaks t={t} setTweak={setTweak}/>
      </ToastProvider>
    );
  }

  if (!state.auth) {
    // Brief blank screen while we ask the server "do I still have a session?"
    // Prevents the login screen from flashing for users who are actually
    // logged in but just reloaded the page.
    if (!authChecked) return null;
    return (
      <ToastProvider>
        <LoginScreen onLogin={(u: AuthUser) => setState((s) => ({ ...s, auth: u }))}/>
        <AppTweaks t={t} setTweak={setTweak}/>
      </ToastProvider>
    );
  }

  const selectedClient = route.page === 'client-detail'
    ? state.clients.find((c) => c.id === (route as any).id)
    : null;
  const crumbs: Array<{ label: string; onClick?: () => void }> = (() => {
    if (route.page === 'dashboard')        return [{ label: 'Dashboard' }];
    if (route.page === 'clients')          return [{ label: 'Clients' }];
    if (route.page === 'client-detail')    return [{ label: 'Clients', onClick: () => nav({ page: 'clients' }) }, { label: (route as any).id }];
    if (route.page === 'global-apps')      return [{ label: 'Global services' }];
    if (route.page === 'global-app-detail') return [{ label: 'Global services', onClick: () => nav({ page: 'global-apps' }) }, { label: (route as any).name }];
    if (route.page === 'nodes')            return [{ label: 'Nodes' }];
    if (route.page === 'bypass-rules')     return [{ label: 'Bypass rules' }];
    if (route.page === 'registries')       return [{ label: 'Registries' }];
    if (route.page === 's3-accounts')      return [{ label: 'Global S3 accounts' }];
    if (route.page === 'minio-servers')  return [{ label: 'Managed S3 servers' }];
    if (route.page === 'access-tokens')    return [{ label: 'Access tokens' }];
    if (route.page === 'audit')            return [{ label: 'Audit log' }];
    if (route.page === 'events')           return [{ label: 'Events' }];
    if (route.page === 'trash')            return [{ label: 'Trash' }];
    if (route.page === 'settings')         return [{ label: 'Settings' }];
    return [{ label: String((route as any).page) }];
  })();

  const navGroups: Array<{ section: string; items: Array<{ id: string; icon: string; label: string; badge?: number }> }> = [
    { section: 'Overview', items: [
      { id: 'dashboard',    icon: 'Dashboard', label: 'Dashboard' },
      { id: 'nodes',        icon: 'Server',    label: 'Nodes' },
    ] },
    { section: 'Clients', items: [
      { id: 'clients',      icon: 'Users',     label: 'Clients', badge: state.clients.length },
      { id: 'global-apps',  icon: 'Globe',     label: 'Global services' },
      { id: 'bypass-rules', icon: 'Lock',      label: 'Bypass rules' },
    ] },
    { section: 'Access & storage', items: [
      { id: 'minio-servers', icon: 'Server',  label: 'Managed S3 servers' },
      { id: 's3-accounts',  icon: 'Cube',      label: 'Global S3 accounts' },
      { id: 'access-tokens', icon: 'Key',      label: 'Access tokens' },
      { id: 'registries',   icon: 'Database',  label: 'Registries' },
    ] },
    { section: 'Activity', items: [
      { id: 'audit',        icon: 'History',   label: 'Audit log' },
      { id: 'events',       icon: 'Activity',  label: 'Events' },
    ] },
    { section: 'Workspace', items: [
      { id: 'trash',        icon: 'Trash',     label: 'Trash' },
      { id: 'settings',     icon: 'Settings',  label: 'Settings' },
    ] },
  ];

  return (
    <ToastProvider>
      <OperatorModeProvider>
      <GlobalTooltip/>
      <div className="app-shell">
        <aside className="sidebar">
          <div className="sidebar-brand">
            <Logo size={26}/>
            <div>
              <div className="brand-name">Shoptefy</div>
              <div className="brand-sub">Master Admin</div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <SidebarNav navGroups={navGroups} routePage={route.page} onNav={nav}/>
          </nav>

          <div className="sidebar-user" onClick={() => nav({ page: 'settings' })}>
            <Avatar name={state.auth.name} size={28}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {state.auth.name}
              </div>
              <div className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {state.auth.email}
              </div>
            </div>
            <button className="btn btn-ghost btn-icon btn-sm" title="Sign out"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Best-effort: don't await; even if BE logout fails, sign out client-side.
                      apiLogout().catch(() => {});
                      setState((s) => ({ ...s, auth: null }));
                      setRoute({ page: 'dashboard' });
                    }}>
              <I.LogOut size={13}/>
            </button>
          </div>
        </aside>

        <main className="main">
          <div className="topbar">
            <div className="crumbs">
              {crumbs.map((c, i) => (
                <Fragment key={i}>
                  {i > 0 && <span className="sep"><I.ChevronR size={11}/></span>}
                  {c.onClick ?
                    <button className="btn btn-ghost btn-sm" style={{ height: 'auto', padding: 0, color: 'var(--text-2)' }}
                            onClick={c.onClick}>{c.label}</button> :
                    <span className={i === crumbs.length - 1 ? 'crumb-last' : ''}>{c.label}</span>}
                </Fragment>
              ))}
            </div>
            <div className="topbar-actions">
              <OperatorChip/>
              <VersionChip/>
              <a className="btn btn-ghost btn-sm"
                 href={((import.meta as any).env?.VITE_API_BASE_URL ?? '') + '/api/v1/docs'}
                 target="_blank" rel="noopener" title="OpenAPI 3.1 spec + Swagger UI">
                <I.FileCode size={13}/> API docs
              </a>
              <a className="btn btn-ghost btn-sm"
                 href="/services-lookup" target="_blank" rel="noopener"
                 title="Client-facing service lookup (BasicUser credentials)">
                <I.Layers size={13}/> Client portal <I.ExtLink size={11}/>
              </a>
              <IconBtn title="Theme" onClick={() => setTweak('theme', t.theme === 'dark' ? 'light' : 'dark')}>
                {t.theme === 'dark' ? <I.Moon size={14}/> : <I.Sun size={14}/>}
              </IconBtn>
              <div style={{ width: 1, height: 18, background: 'var(--border-strong)', margin: '0 6px' }}/>
              <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>
                <I.Plus size={13}/> New client
              </button>
            </div>
          </div>

          <div className="page">
            {route.page === 'dashboard' && <Dashboard state={state} setState={setState} onNav={nav}/>}
            {route.page === 'clients' && <ClientsList state={state} setState={setState} onNav={nav} openAddWizard={() => setAddOpen(true)} loading={!tenantsLoaded}/>}
            {route.page === 'client-detail' && selectedClient && (
              <ClientDetail client={selectedClient} state={state} setState={setState}
                            onNav={nav} route={route}/>
            )}
            {route.page === 'client-detail' && !selectedClient && (
              <Empty title="Client not found" message={`No client with id "${(route as any).id}".`}
                     action={<button className="btn btn-primary" onClick={() => nav({ page: 'clients' })}>Back to list</button>}/>
            )}
            {route.page === 'global-apps' && <GlobalAppsScreen onNav={nav}/>}
            {route.page === 'global-app-detail' && <GlobalAppDetail name={(route as any).name} onNav={nav}/>}
            {route.page === 'nodes' && <NodesScreen/>}
            {route.page === 'bypass-rules' && <BypassRulesScreen state={state}/>}
            {route.page === 'registries' && <RegistriesScreen/>}
            {route.page === 's3-accounts' && <S3AccountsScreen/>}
            {route.page === 'minio-servers' && <OperatorOnly><StorageServersScreen/></OperatorOnly>}
            {route.page === 'access-tokens' && <OperatorOnly><AccessTokensScreen/></OperatorOnly>}
            {route.page === 'audit' && <AuditLog/>}
            {route.page === 'events' && <EventsScreen/>}
            {route.page === 'trash' && <TrashScreen/>}
            {route.page === 'settings' && (
              <Settings state={state} setState={setState}
                        theme={t.theme} setTheme={(v) => setTweak('theme', v as 'dark' | 'light')}
                        accent={t.accent} setAccent={(v) => setTweak('accent', v as 'indigo' | 'emerald' | 'amber')}/>
            )}
          </div>
        </main>
      </div>

      <AddClientWizard open={addOpen} onClose={() => setAddOpen(false)}
                       state={state} setState={setState} onNav={nav}/>

      <AppTweaks t={t} setTweak={setTweak}/>
      </OperatorModeProvider>
    </ToastProvider>
  );
}

// Topbar pill shown only while operator mode is unlocked, so the operator has a
// clear signal the hidden tools are visible (a client never sees it, because it
// renders only when on). Click it to lock back to client view.
function OperatorChip() {
  const { unlocked, toggle } = useOperatorMode();
  if (!unlocked) return null;
  return (
    <button className="btn btn-ghost btn-sm" onClick={toggle}
            title="Operator tools are visible — click to hide (client view)">
      <I.Terminal size={13}/> operator
    </button>
  );
}

function AppTweaks({ t, setTweak }: { t: typeof TWEAK_DEFAULTS; setTweak: any }) {
  return (
    <TweaksPanel>
      <TweakSection label="Appearance"/>
      <TweakRadio label="Theme" value={t.theme}
                  options={['dark', 'light']}
                  onChange={(v) => setTweak('theme', v)}/>
      <TweakRadio label="Accent" value={t.accent}
                  options={['indigo', 'emerald', 'amber']}
                  onChange={(v) => setTweak('accent', v)}/>
    </TweaksPanel>
  );
}
